import { Link } from 'react-router-dom';
import { Activity, AppWindow, ClipboardCheck, Film, Users, Zap, RefreshCw, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { Async, PageHeader } from '../../components/ui/States';
import { StatCard } from '../../components/ui/StatCard';
import { useApi } from '../../hooks/useApi';
import { btnGhost, cardCls, formatDate } from './shared';

interface Metrics {
  totalTitles: number;
  publishedTitles: number;
  pendingTitles: number;
  totalEpisodes: number;
  totalTenants: number;
  activeCreators: number;
  apiCallsToday: number | null;
  playbackRequestsToday: number | null;
}

interface Health {
  checkedAt: string;
  checks: {
    key: string;
    label: string;
    status: 'ok' | 'error' | 'not_configured';
    latencyMs: number | null;
    detail: string | null;
  }[];
}

const NO_TRAFFIC = 'No traffic recorded yet';
const num = (n: number | null) => (n === null ? '—' : n.toLocaleString());

function SystemStatus() {
  const health = useApi<Health>('/admin/health');
  return (
    <div className={`${cardCls} p-5`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-white">System status</h2>
          {health.data && <p className="text-[11px] text-gray-500 mt-0.5">Checked {formatDate(health.data.checkedAt)}</p>}
        </div>
        <button onClick={health.reload} disabled={health.loading} className={`${btnGhost} flex items-center gap-1.5`}>
          <RefreshCw className={`w-3.5 h-3.5 ${health.loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <Async state={health}>
        {(h) =>
          h.checks.length === 0 ? (
            <p className="text-xs text-gray-400">No system checks are reported by the server.</p>
          ) : (
            <ul className="divide-y divide-[#241b33]">
              {h.checks.map((c) => (
                <li key={c.key} className="flex items-start gap-3 py-2.5">
                  {c.status === 'ok' ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                  ) : c.status === 'error' ? (
                    <XCircle className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" />
                  ) : (
                    <MinusCircle className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{c.label}</p>
                    {c.status === 'error' && <p className="text-xs text-rose-300 break-words">{c.detail ?? 'Something is wrong.'}</p>}
                    {c.status === 'ok' && c.latencyMs !== null && <p className="text-xs text-gray-500">Responded in {c.latencyMs} ms</p>}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      c.status === 'ok' ? 'text-emerald-400' : c.status === 'error' ? 'text-rose-400' : 'text-gray-500'
                    }`}
                  >
                    {c.status === 'ok' ? 'Working' : c.status === 'error' ? 'Problem' : 'Not set up'}
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </Async>
    </div>
  );
}

export function Overview() {
  const state = useApi<{ metrics: Metrics }>('/admin/metrics');
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader title="Platform overview" subtitle="A live summary of the catalogue, the apps and creators on the platform, and whether everything is running." />
      <Async state={state}>
        {({ metrics: m }) => (
          <div className="space-y-6">
            {m.pendingTitles > 0 && (
              <Link
                to="/staff/review"
                className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 px-5 py-4 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="w-5 h-5 text-amber-400" />
                  <span className="text-sm font-semibold text-white">
                    {m.pendingTitles} {m.pendingTitles === 1 ? 'title is' : 'titles are'} waiting for review
                  </span>
                </div>
                <span className="text-xs font-semibold text-amber-300">Review now →</span>
              </Link>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                label="Titles"
                value={m.totalTitles.toLocaleString()}
                subtitle={`${m.publishedTitles.toLocaleString()} published · ${m.totalEpisodes.toLocaleString()} episodes`}
                icon={<Film className="w-4 h-4" />}
              />
              <Link to="/staff/review" className="block">
                <StatCard
                  label="Waiting for review"
                  value={m.pendingTitles.toLocaleString()}
                  subtitle="Open the review queue"
                  icon={<ClipboardCheck className="w-4 h-4" />}
                />
              </Link>
              <StatCard label="Apps" value={m.totalTenants.toLocaleString()} subtitle="Apps using the platform" icon={<AppWindow className="w-4 h-4" />} />
              <StatCard label="Active creators" value={m.activeCreators.toLocaleString()} icon={<Users className="w-4 h-4" />} />
              <StatCard
                label="API calls today"
                value={num(m.apiCallsToday)}
                subtitle={m.apiCallsToday === null ? NO_TRAFFIC : undefined}
                icon={<Zap className="w-4 h-4" />}
              />
              <StatCard
                label="Playback requests today"
                value={num(m.playbackRequestsToday)}
                subtitle={m.playbackRequestsToday === null ? NO_TRAFFIC : undefined}
                icon={<Activity className="w-4 h-4" />}
              />
            </div>

            <SystemStatus />
          </div>
        )}
      </Async>
    </div>
  );
}
